import Form from "~/components/resale-catalog/Form";
import type { ImageWidget } from "~/types/widgets";
import Image from "~/components/ui/Image";
import { Picture, Source } from "~/components/ui/Picture";
import Benefits from "./Benefits";
import { type FnContext as FnContext, type SectionProps as SectionProps } from "~/types/deco";
interface FeaturedImage {
    /**
     * @title Imagem
     */
    src: ImageWidget;
    /**
     * @title Texto alternativo
     */
    alt?: string;
}
/** @titleBy alt */
interface Benefit {
    /**
     * @title Imagem
     */
    src: ImageWidget;
    /**
     * @title Texto alternativo
     */
    alt?: string;
}
export interface Benefits {
    /**
     * @title Imagem destacada
     */
    featuredImage?: FeaturedImage;
    /**
     * @title Itens
     */
    items: Benefit[];
}
interface Success {
    /**
     * @title Título
     */
    title?: string;
    /**
     * @title Texto
     * @format textarea
     */
    text?: string;
}
interface Form {
    /**
     * @title Título
     */
    title?: string;
    /**
     * @title Texto
     * @format textarea
     */
    text?: string;
    /**
     * @title Sucesso
     */
    success: Success;
}
interface CatalogImage {
    /**
     * @title Texto
     * @format html
     */
    text?: string;
    /**
     * @title Imagem
     */
    src: ImageWidget;
    /**
     * @title Texto alternativo
     */
    alt?: string;
}
/** @titleBy url */
interface CatalogOption {
    /**
     * @title Nome
     * @format html
     */
    name: string;
    /**
     * @title URL
     */
    url: string;
    /**
     * @title Destacado
     */
    highlight?: boolean;
}
/** @titleBy alt */
export interface Catalog {
    /**
     * @title Imagem
     */
    src: ImageWidget;
    /**
     * @title Texto alternativo
     */
    alt: string;
    /**
     * @title Opções
     */
    options: CatalogOption[];
}
export interface Props {
    /**
     * @title Título
     */
    title: string;
    /**
     * @title Subtítulo
     */
    subtitle?: string;
    /**
     * @title Imagem de fundo
     */
    backgroundImage?: {
        /**
         * @title Imagem desktop
         */
        desktop: ImageWidget;
        /**
         * @title Imagem mobile
         */
        mobile: ImageWidget;
    };
    /**
     * @title Formulário
     */
    form: Form;
    /**
     * @title Catátalogos
     */
    catalogs: Catalog[];
    /**
     * @title WhatsApp
     */
    whatsapp?: string;
    /**
     * @title Imagem do catálogo
     */
    catalog?: CatalogImage;
    /**
     * @title Benefícios
     */
    benefits?: Benefits;
}
const formatWhatsAppUrl = (whatsapp: string) => {
    let number = whatsapp.replace(/\D/g, '');
    number = number.startsWith('55') ? number : `55${number}`;
    return `https://wa.me/${number}`;
};
export default function Resale({ title, subtitle, backgroundImage, form, catalogs, whatsapp, catalog, benefits, isMobileDevice, }: SectionProps<typeof loader>) {
    return (<div className='bg-[#351d39] relative'>
            {backgroundImage && (<Picture>
                    <Source src={backgroundImage?.mobile} media='(max-width: 1024px)' width={640} height={810}/>

                    <Source src={backgroundImage?.desktop} media='(min-width: 1025px)' width={1920} height={1140}/>

                    <img className='w-full max-w-md ml-auto object-top lg:max-w-none lg:h-full lg:pb-40 lg:absolute lg:top-0 lg:bottom-0 lg:object-cover' src={backgroundImage?.desktop} alt='Imagem de fundo' aria-hidden loading='lazy'/>
                </Picture>)}

            <div className=' py-8 px-3 -mt-56 mx-auto flex flex-col lg:flex-row justify-between gap-16 md:max-w-lg md:-mt-72 w-[95%] lg:max-w-[1320px] lg:mt-0 lg:gap-8 lg:relative'>
                {/* Left */}
                <div className='w-full flex flex-col justify-center gap-10 lg:gap-20'>
                    <div>
                        <h1 className='resale-text-gradient text-5xl leading-tight font-bold'>
                            {title ?? 'Seja uma revendedora'}
                        </h1>

                        {subtitle && (<small className='text-white text-2xl font-barlow font-semibold min-[1600px]:text-[40px]'>
                                {subtitle}
                            </small>)}
                    </div>

                    <Form form={form} catalogs={catalogs}/>

                    {whatsapp && (<div className='w-max mx-auto pt-6 grid justify-items-center gap-4 lg:mx-0'>
                            <span className='w-80 h-12 rounded-full lg:rounded-xl bg-[#871e4f99] text-white text-lg font-semibold uppercase flex justify-center items-center lg:text-xl'>
                                Atendimento exclusivo
                            </span>

                            <a className='resale-text-gradient !bg-gradient-to-tl text-2xl !font-medium font-barlow flex items-center gap-3 lg:text-3xl' href={formatWhatsAppUrl(whatsapp)} target='_blank' rel='noreferrer'>
                                {whatsapp}
                                <img className='w-9 h-9 lg:w-max lg:h-max' src='https://miess.vteximg.com.br/arquivos/whatsapp-revenda.png' alt='WhatsApp' loading='lazy'/>
                            </a>
                        </div>)}
                </div>

                {/* Right */}
                {catalog && (<div id='catalogs' className='w-full grid justify-items-center gap-2 lg:justify-items-start lg:self-end'>
                        {catalog.text && (<div className='text-white text-lg text-center font-barlow [&_span]:font-medium sm:text-3xl lg:text-left' dangerouslySetInnerHTML={{ __html: catalog.text }}/>)}

                        <Image className='lg:-ml-8' src={catalog.src} alt={catalog.alt} width={580} height={375} loading='lazy'/>
                    </div>)}
            </div>

            <Benefits benefits={benefits} isMobileDevice={isMobileDevice}/>
        </div>);
}
export const loader = (props: Props, _: Request, ctx: FnContext) => {
    const isMobileDevice = ctx.device !== 'desktop';
    return {
        ...props,
        isMobileDevice,
    };
};
